
/**
 * Copyright (c) 2026 FirstSource. All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This file is part of proprietary software owned by FirstSource.
 * Unauthorized copying, distribution, modification, or use of this file,
 * in whole or in part, via any medium, is strictly prohibited without
 * prior written permission from FirstSource.
 *
 * Generated with GitHub Copilot Business/Enterprise.
 * Reviewed and approved by: [Developer Name / Date]
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getCookie } from '../login/cookies';
import { constants } from '../../utils/constants';
import TaskStatusModal from '../outputJson/taskStatusModal';

const ALLOWED_FILE_TYPES = [
    // IMAGE
    'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
    // AUDIO
    'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/x-aac', 'audio/x-m4a', 'audio/mp4',
    // EML
    'message/rfc822',
    // DOC
    'application/pdf', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    // VIDEO
    'video/mp4', 'video/mpeg', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/avi', 'video/msvideo', 'video/x-avi',
];

/** Maps browser MIME types to the backend data_type enum values. */
const MIME_TO_DATA_TYPE: Record<string, string> = {
    'image/png': 'IMAGE', 'image/jpeg': 'IMAGE', 'image/gif': 'IMAGE',
    'image/webp': 'IMAGE', 'image/heic': 'IMAGE', 'image/heif': 'IMAGE',
    'audio/mpeg': 'AUDIO', 'audio/wav': 'AUDIO', 'audio/ogg': 'AUDIO',
    'audio/aac': 'AUDIO', 'audio/x-aac': 'AUDIO', 'audio/x-m4a': 'AUDIO', 'audio/mp4': 'AUDIO',
    'message/rfc822': 'EML',
    'application/pdf': 'DOC', 'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOC',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'DOC',
    'application/vnd.ms-excel': 'DOC',
    'video/mp4': 'VIDEO', 'video/mpeg': 'VIDEO', 'video/webm': 'VIDEO',
    'video/quicktime': 'VIDEO', 'video/x-msvideo': 'VIDEO', 'video/avi': 'VIDEO', 'video/msvideo': 'VIDEO', 'video/x-avi': 'VIDEO',
};

interface ValidationResult {
    passLevel: 'FULL_PASS' | 'PARTIAL_PASS' | 'FAIL';
    failureReason?: string;
}

/** Maps an uppercase format string (e.g. 'PNG') to its accepted MIME types. */
const FORMAT_TO_MIME: Record<string, string[]> = {
    PDF:  ['application/pdf'],
    DOCX: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    XLSX: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    XLS:  ['application/vnd.ms-excel'],
    JPEG: ['image/jpeg'],
    JPG:  ['image/jpeg'],
    EML:  ['message/rfc822'],
    PNG:  ['image/png'],
    DOC:  ['application/msword'],
    HEIF: ['image/heif'],
    HEIC: ['image/heic'],
    MP3:  ['audio/mpeg'],
    WAV:  ['audio/wav'],
    AAC:  ['audio/aac'],
    MP4:  ['video/mp4'],
    MOV:  ['video/quicktime'],
    AVI:  ['video/x-msvideo'],
    HEVC: ['video/quicktime'],
};

/** Maps an uppercase format string to its file extension for the accept attribute. */
const FORMAT_TO_EXT: Record<string, string> = {
    PDF: '.pdf', DOCX: '.docx', XLSX: '.xlsx', XLS: '.xls', JPEG: '.jpeg', JPG: '.jpg',
    EML: '.eml', PNG: '.png', DOC: '.doc', HEIF: '.heif', HEIC: '.heic',
    MP3: '.mp3', WAV: '.wav', AAC: '.aac', MP4: '.mp4', MOV: '.mov', AVI: '.avi', HEVC: '.hevc',
};

export interface TestAiValidationModalProps {
    open: boolean;
    onClose: () => void;
    /** Pre-filled from Advanced AI Settings cpv_prompt — editing here does NOT affect the saved settings. */
    initialPrompt?: string;
    /** The sub-project (project) UUID — sent as project_id in the validation payload. */
    projectId?: string;
    /** The config UUID — sent as config_id in the validation payload. */
    configId?: string;
    /** File formats selected in Advanced AI Settings (e.g. ['PNG', 'JPG', 'PDF']). When provided, only these formats are accepted. */
    allowedFormats?: string[];
    /** Whether the custom prompt validation is enabled in Advanced AI Settings. When false, the prompt field is hidden. */
    isCustomPromptEnabled?: boolean;
}

const TestAiValidationModal: React.FC<TestAiValidationModalProps> = ({
    open, onClose, initialPrompt = '', projectId, configId, allowedFormats, isCustomPromptEnabled = false,
}) => {
    const effectiveMimeTypes: string[] = allowedFormats && allowedFormats.length > 0
        ? [...new Set(allowedFormats.flatMap(f => FORMAT_TO_MIME[f.toUpperCase()] ?? []))]
        : ALLOWED_FILE_TYPES;
    const acceptAttr: string = allowedFormats && allowedFormats.length > 0
        ? [...new Set(allowedFormats.map(f => FORMAT_TO_EXT[f.toUpperCase()]).filter(Boolean))].join(',')
        : '.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.mp3,.wav,.ogg,.aac,.m4a,.eml,.pdf,.doc,.docx,.mp4,.mpeg,.webm,.mov,.avi';
    const formatDisplayText: string = allowedFormats && allowedFormats.length > 0
        ? allowedFormats.join(', ')
        : 'PNG, JPG, PDF, audio, video, EML, DOCX';
    const [testPrompt, setTestPrompt] = useState(initialPrompt);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [pollingModalOpen, setPollingModalOpen] = useState(false);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Sync prompt if initialPrompt changes while dialog is closed
    useEffect(() => {
        if (!open) setTestPrompt(initialPrompt);
    }, [open, initialPrompt]);

    // Clean up polling interval on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, []);

    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    const startPolling = (taskId: string) => {
        const poll = async () => {
            try {
                const res = await fetch(
                    `${constants.API_BASE_URL}project/company/project/output-json/tasks/status?celery_task_id=${taskId}`,
                    { headers: { 'Authorization': `Bearer ${getCookie('token')}` } }
                );
                const data = await res.json();
                const status = data?.data?.status;

                if (status === 'FULL_PASS' || status === 'PARTIAL_PASS' || status === 'FAIL' || status === 'SUCCESS') {
                    stopPolling();
                    setPollingModalOpen(false);
                    const resultData = data?.data ?? {};
                    const passLevel: 'FULL_PASS' | 'PARTIAL_PASS' | 'FAIL' =
                        status === 'SUCCESS' || status === 'FULL_PASS' ? 'FULL_PASS'
                        : status === 'PARTIAL_PASS' ? 'PARTIAL_PASS'
                        : 'FAIL';
                    setValidationResult({
                        passLevel,
                        failureReason:
                            resultData?.result?.reasoning?.trim() ||
                            resultData.failure_reason ||
                            resultData.failureReason,
                    });
                } else if (status === 'TASK_FAILED') {
                    stopPolling();
                    setPollingModalOpen(false);
                    const failedData = data?.data ?? {};
                    const reasoning: string =
                        failedData?.result?.reasoning?.trim() ||
                        data?.message?.trim() ||
                        failedData?.result?.error?.trim() ||
                        'AI validation task failed unexpectedly. Please try again.';
                    setValidationResult({ passLevel: 'FAIL', failureReason: reasoning });
                }
            } catch {
                stopPolling();
                setPollingModalOpen(false);
                setUploadError('Failed to check validation status. Please try again.');
            }
        };

        poll(); // fire immediately, then on interval
        pollingIntervalRef.current = setInterval(poll, 5000);
    };

    const resetState = () => {
        stopPolling();
        setPollingModalOpen(false);
        setSelectedFile(null);
        setUploadedFileUrl(null);
        setValidationResult(null);
        setUploadError(null);
        setIsVerifying(false);
        setIsUploading(false);
        setTestPrompt(initialPrompt);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const validateFile = (file: File): string | null => {
        const fileExt = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
        const mimeOk = !!file.type && effectiveMimeTypes.includes(file.type);
        const extOk = allowedFormats && allowedFormats.length > 0
            ? allowedFormats.some(f => FORMAT_TO_EXT[f.toUpperCase()] === fileExt)
            : Object.values(FORMAT_TO_EXT).includes(fileExt);
        if (!mimeOk && !extOk) {
            return `Unsupported file type. Allowed: ${formatDisplayText}.`;
        }
        return null;
    };

    const handleFileSelect = async (file: File) => {
        const error = validateFile(file);
        if (error) {
            setUploadError(error);
            setSelectedFile(null);
            return;
        }
        setUploadError(null);
        setValidationResult(null);
        setSelectedFile(file);
        setUploadedFileUrl(null);
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(`${constants.API_BASE_URL}upload/company/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${getCookie('token')}` },
                body: formData,
            });
            if (!response.ok) {
                throw new Error(`Upload failed with status ${response.status}`);
            }
            const data = await response.json();
            setUploadedFileUrl(data.file_url);
        } catch {
            setUploadError('Failed to upload file. Please try again.');
            setSelectedFile(null);
        } finally {
            setIsUploading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFileSelect(file);
        e.target.value = '';
    };

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback(() => setIsDragging(false), []);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileSelect(file);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleVerify = async () => {
        if (!selectedFile || !uploadedFileUrl) return;
        setIsVerifying(true);
        setUploadError(null);
        try {
            const fileExt = selectedFile.name.split('.').pop()?.toUpperCase() ?? '';
            const dataTypeByExt = FORMAT_TO_MIME[fileExt]
                ? MIME_TO_DATA_TYPE[FORMAT_TO_MIME[fileExt][0]]
                : undefined;
            const dataType = MIME_TO_DATA_TYPE[selectedFile.type] ?? dataTypeByExt ?? 'IMAGE';
            const response = await fetch(`${constants.API_BASE_URL}project/company/config/ai-validation/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getCookie('token')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    project_id: projectId,
                    file_url: uploadedFileUrl,
                    config_id: configId,
                    data_type: dataType,
                    ...(isCustomPromptEnabled && testPrompt ? { custom_prompt: testPrompt } : {}),
                }),
            });
            if (!response.ok) {
                throw new Error(`Validation request failed with status ${response.status}`);
            }
            const data = await response.json();
            if (data.status === true && data.data?.task_id) {
                setPollingModalOpen(true);
                startPolling(data.data.task_id);
            } else {
                // Synchronous result (fallback)
                const rawStatus = data?.data?.status ?? data?.status;
                const passLevel: 'FULL_PASS' | 'PARTIAL_PASS' | 'FAIL' =
                    rawStatus === 'FULL_PASS' ? 'FULL_PASS'
                    : rawStatus === 'PARTIAL_PASS' ? 'PARTIAL_PASS'
                    : 'FAIL';
                setValidationResult({
                    passLevel,
                    failureReason: data.failure_reason ?? data.failureReason,
                });
            }
        } catch {
            setUploadError('Verification failed. Please try again.');
        } finally {
            setIsVerifying(false);
        }
    };

    const PASS_LEVEL_CONFIG = {
        FULL_PASS:    { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', label: 'Full Pass',    icon: 'check' },
        PARTIAL_PASS: { color: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'Partial Pass', icon: 'warning' },
        FAIL:         { color: '#dc2626', bg: '#fff1f2', border: '#fecaca', label: 'Failed',        icon: 'error' },
    };
    const levelCfg = validationResult
        ? (PASS_LEVEL_CONFIG[validationResult.passLevel] ?? PASS_LEVEL_CONFIG.FAIL)
        : PASS_LEVEL_CONFIG.FAIL;
    const canVerify = !!selectedFile && !!uploadedFileUrl && !isUploading && !isVerifying && !pollingModalOpen && !validationResult;

    return (
        <>
            <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                },
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ px: 3, pt: 2.5, pb: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: 'Montserrat',
                                fontWeight: 700,
                                fontSize: '17px',
                                color: '#0f172a',
                                lineHeight: 1.3,
                            }}
                        >
                            Test Advanced Validation
                        </Typography>
                        <Typography
                            sx={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#64748b', mt: 0.4 }}
                        >
                            Upload an image and verify it against your AI validation rules.
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleClose} aria-label="Close dialog" sx={{ mt: -0.5, mr: -0.5 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pt: 2, pb: 3 }}>

                {/* Prompt field — only shown when custom prompt validation is enabled in Advanced AI Settings */}
                {isCustomPromptEnabled && <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        <Typography
                            sx={{
                                fontFamily: 'Montserrat',
                                fontSize: '12px',
                                fontWeight: 700,
                                color: '#374151',
                            }}
                        >
                            Validation Prompt
                        </Typography>
                        <Tooltip
                            title="Pre-filled from your Advanced AI Settings. Changes here are for this test only and won't affect the saved settings."
                            placement="right"
                            arrow
                        >
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: '#9ca3af', cursor: 'default' }} />
                        </Tooltip>
                    </Box>
                    <TextField
                        multiline
                        rows={3}
                        fullWidth
                        size="small"
                        placeholder="Enter the validation prompt to test..."
                        value={testPrompt}
                        onChange={e => setTestPrompt(e.target.value)}
                        sx={{
                            '& .MuiInputBase-input': { fontSize: '12px', fontFamily: 'Montserrat' },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                backgroundColor: '#f8fafc',
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3D85C6' },
                            },
                        }}
                    />
                </Box>}

                <Divider sx={{ mb: 2.5 }} />

                {/* Upload area */}
                <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    sx={{
                        border: `2px dashed ${isDragging ? '#3D85C6' : selectedFile ? '#3D85C6' : '#cbd5e1'}`,
                        borderRadius: 2,
                        p: 2.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: isDragging ? '#eff6ff' : selectedFile ? '#f0f7ff' : '#f8fafc',
                        cursor: isUploading ? 'default' : 'pointer',
                        transition: 'all 0.2s',
                        mb: uploadError ? 1.5 : 2,
                    }}
                >
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            backgroundColor: selectedFile ? '#dbeafe' : '#e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s',
                        }}
                    >
                        {selectedFile
                            ? <InsertDriveFileOutlinedIcon sx={{ color: '#3D85C6', fontSize: 24 }} />
                            : <CloudUploadOutlinedIcon sx={{ color: '#94a3b8', fontSize: 24 }} />
                        }
                    </Box>

                    {selectedFile ? (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography
                                sx={{
                                    fontFamily: 'Montserrat',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#1e40af',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {isUploading ? 'Uploading…' : selectedFile.name}
                            </Typography>
                            {!isUploading && (
                                <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', color: '#64748b', mt: 0.25 }}>
                                    {(selectedFile.size / 1024).toFixed(1)} KB · Click to change
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <>
                            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 500, color: '#475569' }}>
                                Click to upload or drag and drop
                            </Typography>
                            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', color: '#94a3b8' }}>
                                {formatDisplayText}
                            </Typography>
                        </>
                    )}

                    <Button
                        variant="outlined"
                        size="small"
                        disabled={isUploading}
                        onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
                        startIcon={isUploading ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : null}
                        sx={{
                            mt: 0.5,
                            borderColor: '#3D85C6',
                            color: '#3D85C6',
                            fontFamily: 'Montserrat',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 5,
                            px: 2.5,
                            '&:hover': { borderColor: '#2563eb', backgroundColor: '#eff6ff' },
                            '&.Mui-disabled': { borderColor: '#cbd5e1', color: '#94a3b8' },
                        }}
                    >
                        {isUploading ? 'Uploading…' : selectedFile ? 'Change File' : 'Select File'}
                    </Button>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={acceptAttr}
                        style={{ display: 'none' }}
                        onChange={handleInputChange}
                        aria-label="Upload image for AI validation"
                    />
                </Box>

                {uploadError && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.75,
                            mb: 2,
                            p: 1.25,
                            borderRadius: 1.5,
                            backgroundColor: '#fff1f2',
                            border: '1px solid #fecaca',
                        }}
                    >
                        <ErrorOutlineIcon sx={{ fontSize: 15, color: '#dc2626', mt: '1px', flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#dc2626' }}>
                            {uploadError}
                        </Typography>
                    </Box>
                )}

                {/* Verify button */}
                <Button
                    fullWidth
                    variant="contained"
                    disabled={!canVerify}
                    onClick={handleVerify}
                    startIcon={
                        isVerifying
                            ? <CircularProgress size={17} sx={{ color: 'inherit' }} />
                            : <TaskAltIcon sx={{ fontSize: 19 }} />
                    }
                    sx={{
                        background: canVerify ? 'linear-gradient(135deg, #3D85C6 0%, #2563eb 100%)' : undefined,
                        backgroundColor: canVerify ? undefined : '#e2e8f0',
                        color: canVerify ? '#fff' : '#94a3b8',
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        fontWeight: 700,
                        textTransform: 'none',
                        py: 1.4,
                        borderRadius: 2,
                        boxShadow: canVerify ? '0 4px 14px rgba(61,133,198,0.35)' : 'none',
                        '&:hover': {
                            background: canVerify ? 'linear-gradient(135deg, #2f6ba0 0%, #1d4ed8 100%)' : undefined,
                            boxShadow: canVerify ? '0 6px 18px rgba(61,133,198,0.4)' : 'none',
                        },
                        '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
                        mb: 2,
                        transition: 'all 0.2s',
                    }}
                >
                    {isVerifying ? 'Validating...' : 'Validate'}
                </Button>

                {/* Validation result */}
                {validationResult && (
                    <Box
                        sx={{
                            border: `1px solid ${levelCfg.border}`,
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: levelCfg.bg,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: validationResult.failureReason ? 1 : 0 }}>
                            {levelCfg.icon === 'check'
                                ? <CheckCircleOutlineIcon sx={{ color: levelCfg.color, fontSize: 20 }} />
                                : <ErrorOutlineIcon sx={{ color: levelCfg.color, fontSize: 20 }} />
                            }
                            <Typography
                                sx={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '13px', color: levelCfg.color }}
                            >
                                {levelCfg.label}
                            </Typography>
                            <Box
                                sx={{
                                    ml: 'auto',
                                    px: 1.25,
                                    py: 0.25,
                                    borderRadius: 10,
                                    backgroundColor: levelCfg.border,
                                    border: `1px solid ${levelCfg.border}`,
                                }}
                            >
                                <Typography
                                    sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 700, color: levelCfg.color }}
                                >
                                    {validationResult.passLevel.replace('_', ' ')}
                                </Typography>
                            </Box>
                        </Box>

                        {validationResult.failureReason && (
                            <>
                                <Typography
                                    sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 700, color: levelCfg.color, mb: 0.4 }}
                                >
                                    Reason:
                                </Typography>
                                <Typography
                                    sx={{ fontFamily: 'Montserrat', fontSize: '12px', color: levelCfg.color, mb: 1.5, lineHeight: 1.6 }}
                                >
                                    {validationResult.failureReason}
                                </Typography>
                            </>
                        )}

                        <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', width: 'fit-content', mt: 1.5 }}
                            onClick={resetState}
                            role="button"
                            aria-label="Test again"
                        >
                            <RefreshIcon sx={{ fontSize: 14, color: levelCfg.color }} />
                            <Typography
                                sx={{ fontFamily: 'Montserrat', fontSize: '12px', color: levelCfg.color, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                            >
                                Test again
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>

        {/* Processing modal — shown while polling task status */}
        <TaskStatusModal
            open={pollingModalOpen}
            onClose={() => { stopPolling(); setPollingModalOpen(false); }}
            taskStatusData={{ status: 'PENDING' }}
            completedTaskExported
        />
        </>
    );
};

export default TestAiValidationModal;

